import React, { useState } from 'react';
import { Shield, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { setUpTOTP, verifyTOTPSetup } from 'aws-amplify/auth';

export default function TwoFactorSetup({ onComplete }) {
  const [step, setStep] = useState('setup'); // 'setup', 'verify', 'complete'
  const [qrCode, setQrCode] = useState(null);
  const [secretKey, setSecretKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSetup = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Note: This requires Amplify Auth v6 API
      // The actual implementation depends on Amplify version
      // For now, this is a placeholder that shows the UI flow
      
      // In production, use:
      // const { sharedSecret, getQRCode } = await setUpTOTP();
      // setSecretKey(sharedSecret);
      // setQrCode(getQRCode());
      
      // Simulated for now - replace with actual API call
      setSecretKey('EXAMPLE_SECRET_KEY_123456');
      setStep('verify');
    } catch (err) {
      setError(err.message || 'Failed to set up 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Verify TOTP code
      // await verifyTOTPSetup({ code: verificationCode });
      
      setStep('complete');
      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      setError('Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'complete') {
    return (
      <div className="rounded-2xl border border-green-500/30 bg-green-500/10 p-6 text-center">
        <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">2FA Enabled Successfully!</h3>
        <p className="text-slate-300">
          Your account is now protected with two-factor authentication.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6">
      <div className="flex items-center gap-3 mb-4">
        <Shield className="h-6 w-6 text-green-400" />
        <h2 className="text-xl font-bold text-white">Set Up Two-Factor Authentication</h2>
      </div>

      {error && (
        <div className="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {step === 'setup' && (
        <div className="space-y-4">
          <p className="text-slate-300">
            2FA adds an extra layer of security to your account. You'll use an authenticator app 
            (like Google Authenticator or Authy) to generate codes.
          </p>
          <button
            onClick={handleSetup}
            disabled={loading}
            className="w-full px-6 py-3 rounded-xl bg-green-500 text-white font-bold hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Setting up...
              </>
            ) : (
              <>
                <Shield className="h-5 w-5" />
                Set Up 2FA
              </>
            )}
          </button>
        </div>
      )}

      {step === 'verify' && (
        <div className="space-y-4">
          <p className="text-slate-300">
            Scan this QR code with your authenticator app, then enter the 6-digit code to verify.
          </p>
          
          {qrCode && (
            <div className="flex justify-center p-4 bg-white rounded-lg">
              <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
            </div>
          )}

          {secretKey && (
            <div className="p-4 rounded-lg bg-slate-800 border border-slate-700">
              <p className="text-xs text-slate-400 mb-2">Or enter this key manually:</p>
              <code className="text-sm text-white font-mono">{secretKey}</code>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">
              Enter 6-digit code from your authenticator app
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <button
            onClick={handleVerify}
            disabled={loading || verificationCode.length !== 6}
            className="w-full px-6 py-3 rounded-xl bg-green-500 text-white font-bold hover:bg-green-600 transition-colors disabled:opacity-50"
          >
            {loading ? 'Verifying...' : 'Verify and Enable'}
          </button>
        </div>
      )}
    </div>
  );
}
